import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  useFormContext,
  ModalFormButton,
  CurrencyInput,
  Dropdown,
  DropdownOption,
  formatCurrencyValue,
} from 'framework/form';
import { AvailableLocationToQualification, AvailableQualificationToLocation } from '../types';
import { useAsyncValue } from 'framework/utils';
import { useAuthorization } from 'framework/authorization';
import { FETCH_ALL_EMPLOYEES_SALARIES, CALCULATE_EMPLOYEE_PERCENTILE, EMPLOYEE_COMPETENCY_LEVEL } from '../constants';
import {
  PROJECT_MANAGER_ID,
  BUSINESS_ANALYST_ID,
  QA_ENGINEER_ID,
  SOFTWARE_ENGINEER_ID,
} from 'domain/shared/employee/position/positionService';
import PositionWithTechnologyDropdown from 'domain/shared/employee/position/input/PositionWithTechnologyDropdown';
import useOfficeService, {
  OFFICE_NAMES,
  type OfficeType,
  MINSK_OFFICE_ID,
  KRAKOW_OFFICE_ID,
} from 'domain/shared/employee/officeService';
import BaseAmountTypeDropdown from 'domain/shared/employee/input/BaseAmountTypeDropdown';
import { BASE_AMOUNT_TYPES, BASE_AMOUNT_TYPES_ALIASES } from 'domain/shared/employee/finance/constants';
import usePerformanceMeasurementService from '../performanceMeasurementService';
import { Button, Tooltip } from 'framework/ui/mui-builder';
import { Box, Typography, ButtonProps } from '@mui/material';
import { CurrencyDropdown } from 'domain/shared/core/input';
import { usePeopleManagementApi } from 'infrastructure/api';
import calculatePrecentileShema from './calculatePrecentileShema';
import { CURRENCY_TYPES } from '../../core/constants';

const RESTRICTED_POSITION_IDS = [PROJECT_MANAGER_ID, BUSINESS_ANALYST_ID, QA_ENGINEER_ID, SOFTWARE_ENGINEER_ID];

const { BYN, ...DROPDOWN_CURRENCY_TYPES } = CURRENCY_TYPES;

interface CalculatePercentileModalButtonProps {
  initialPosition?: string | null;
  size?: ButtonProps['size'];
}
const RESTRICTED_OFFICE_IDS = [MINSK_OFFICE_ID, KRAKOW_OFFICE_ID];
const RESTRICTED_COMPETENCY_LEVELS: string[] = [
  EMPLOYEE_COMPETENCY_LEVEL.L1,
  EMPLOYEE_COMPETENCY_LEVEL.L2,
  EMPLOYEE_COMPETENCY_LEVEL.L3,
];

interface CalculateFormProps {
  percentile: string;
  showPercentData: CalculateFormData | null;
  hasFullAccess: boolean;
}

interface CalculateFormData {
  positionId: string;
  specialization?: string;
  competencyLevel: string;
  officeId?: string;
  compensationType: string;
  compensationSum: number | string;
  currency: string;
}

type LookupData = {
  positions: any[] | null;
  offices: OfficeType[];
  availableLocationsToQualification?: AvailableLocationToQualification;
  availableQualificationToLocation?: AvailableQualificationToLocation;
};

const formatCurrency = (amount: number | string) =>
  Number(amount)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const enhanceOptionsWithTooltips = (
  options: DropdownOption[],
  tooltipMessage: string = 'Not enough data to calculate percentile.',
): DropdownOption[] => {
  return options.map((option, index) => {
    if ('options' in option) {
      return option;
    }

    if (!option.disabled) {
      return option;
    }

    const tooltipId = `tooltip-${option.value}-${index}`;

    return {
      ...option,
      label: (
        <Tooltip title={tooltipMessage} id={tooltipId}>
          <span id={tooltipId}>
            <Box>{option.label}</Box>
          </span>
        </Tooltip>
      ),
    };
  });
};

const CalculateForm: React.FC<CalculateFormProps> = ({ percentile, showPercentData, hasFullAccess }) => {
  const { getPercentileCalculationByPosition, getOfficeLocationOptions, getCompetencyLevelOptions } =
    usePerformanceMeasurementService();
  const { submitForm, data: formData } = useFormContext();
  const { findOfficeById, findAll: findAllOffices } = useOfficeService();
  const offices = useAsyncValue(findAllOffices);

  const initialLookupData: LookupData = {
    positions: formData?.positionsAndTechnologies || [],
    offices: [],
    availableLocationsToQualification: undefined,
    availableQualificationToLocation: undefined,
  };

  const [lookupData, setLookupData] = useState<LookupData>(initialLookupData);
  const selectedOffice = useAsyncValue(() => findOfficeById(Number(formData?.officeId)), [formData?.officeId]);
  const selectedOfficeName = OFFICE_NAMES[selectedOffice?.name || ''];

  useEffect(() => {
    if (!formData?.position) return;
    getPercentileCalculationByPosition(String(formData?.position))
      .then(percentileCalculation => {
        setLookupData(prev => ({
          ...prev,
          availableLocationsToQualification: percentileCalculation.availableLocationsToQualification,
          availableQualificationToLocation: percentileCalculation.availableQualificationToLocation,
        }));
      })
      // eslint-disable-next-line no-console
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.position]);

  const competencyLevelOptions = useMemo(() => {
    const options = getCompetencyLevelOptions(
      lookupData.availableLocationsToQualification,
      lookupData.availableQualificationToLocation,
      selectedOfficeName,
    );
    if (hasFullAccess) return options;
    return options.filter(opt => RESTRICTED_COMPETENCY_LEVELS.includes(String(opt.value)));
  }, [
    lookupData.availableLocationsToQualification,
    lookupData.availableQualificationToLocation,
    selectedOfficeName,
    hasFullAccess,
    getCompetencyLevelOptions,
  ]);

  const availableLocationToQualification = useMemo(() => {
    const options = getOfficeLocationOptions(
      offices,
      lookupData.availableLocationsToQualification,
      lookupData.availableQualificationToLocation,
      formData?.competencyLevel || undefined,
    );
    if (hasFullAccess) return options;
    // Filter to only Minsk (2) and Krakow (3) for restricted access
    return options.filter(opt => RESTRICTED_OFFICE_IDS.includes(Number(opt.value)));
  }, [
    lookupData.availableLocationsToQualification,
    lookupData.availableQualificationToLocation,
    formData?.competencyLevel,
    getOfficeLocationOptions,
    offices,
    hasFullAccess,
  ]);

  const enhancedCompetencyLevelOptions = useMemo(
    () => enhanceOptionsWithTooltips(competencyLevelOptions),
    [competencyLevelOptions],
  );

  const enhancedOfficeOptions = useMemo(
    () => enhanceOptionsWithTooltips(availableLocationToQualification),
    [availableLocationToQualification],
  );

  const renderResult = useMemo(() => {
    if (!percentile || !showPercentData) return null;
    const { positions } = lookupData;
    const { currency, compensationSum, compensationType, competencyLevel, specialization, positionId, officeId } =
      showPercentData;
    const technologyAndPositionName =
      positions?.find(p => {
        return (
          String(p.id) === String(positionId) &&
          (String(p.technologyId) ? String(p.technologyId) === String(specialization) : true)
        );
      })?.label || '';

    if (typeof percentile === 'string' && percentile?.includes('-')) {
      return (
        <Box mt="16px">
          <Typography>
            Salary falls in the <b>{percentile}</b> bucket among{' '}
            <b>
              {technologyAndPositionName}
              {competencyLevel ? `${competencyLevel} ` : ''}
            </b>
            {officeId ? (
              <>
                {' '}
                in <b>{selectedOfficeName}</b>.
              </>
            ) : (
              '.'
            )}
          </Typography>
        </Box>
      );
    }

    return (
      <Box mt="16px">
        <Typography>
          The compensation of{' '}
          <b>
            {currency} {formatCurrency(compensationSum)}
          </b>{' '}
          Gross {BASE_AMOUNT_TYPES[compensationType as keyof typeof BASE_AMOUNT_TYPES]} places the worker in the{' '}
          <b>{percentile}th</b> percentile among{' '}
          <b>
            {competencyLevel ? `${competencyLevel} ` : ''}
            {' ' + technologyAndPositionName}
          </b>
          {officeId ? (
            <>
              {' '}
              in <b>{selectedOfficeName}</b>.
            </>
          ) : (
            '.'
          )}
        </Typography>
      </Box>
    );
  }, [percentile, showPercentData, lookupData, selectedOfficeName]);

  return (
    <>
      <Box display="flex" justifyContent="space-between" gap="20px">
        {lookupData.positions && (
          <PositionWithTechnologyDropdown
            source="position"
            label="Specialization"
            providedOptions={lookupData.positions}
          />
        )}
        <Dropdown source="competencyLevel" label="Level" placeholder="Level" options={enhancedCompetencyLevelOptions} />
        <Dropdown source="officeId" label="Office" placeholder="Select office" options={enhancedOfficeOptions} />
      </Box>

      <Box display="flex" justifyContent="space-between" gap="20px">
        <BaseAmountTypeDropdown source="compensationType" label="Compensation type" />
        <CurrencyInput formatInitialValue={formatCurrencyValue} label="Compensation sum" source="compensationSum" />
        <CurrencyDropdown
          options={{ ...DROPDOWN_CURRENCY_TYPES, BYR: 'BYN' }}
          source="currency"
          label="Currency"
          placeholder=""
        />
      </Box>

      <Box mt="20px">
        <Button onClick={submitForm}>Calculate</Button>
      </Box>

      {renderResult}
    </>
  );
};

const CalculatePercentileModalButton: React.FC<CalculatePercentileModalButtonProps> = ({
  initialPosition,
  size = 'medium',
}) => {
  const { sendPostRequest } = usePeopleManagementApi();
  const { getPercentileCalculation } = usePerformanceMeasurementService();
  const { isGranted } = useAuthorization();
  const [percentile, setPercentile] = useState('');
  const [showPercentData, setShowPercentData] = useState<CalculateFormData | null>(null);

  const hasFullAccess = isGranted(FETCH_ALL_EMPLOYEES_SALARIES);
  const canCalculatePercentile = isGranted(CALCULATE_EMPLOYEE_PERCENTILE);

  const calculateAction = useCallback(
    async (formData: any) => {
      setPercentile('');
      setShowPercentData(null);

      const positionValue = String(formData.position);
      const [positionId, specialization] = positionValue.includes('--')
        ? positionValue.split('--')
        : [positionValue, undefined];

      const requestData: CalculateFormData = {
        positionId,
        specialization,
        competencyLevel: formData.competencyLevel,
        officeId: formData.officeId || null,
        compensationType: formData.compensationType,
        compensationSum: formData.compensationSum,
        currency: formData.currency,
      };

      const response = await sendPostRequest('/employees/performance-rank/percentile', requestData);
      const json = await response.json();

      setShowPercentData(requestData);
      setPercentile(String(json.percentile));

      return json;
    },
    [sendPostRequest],
  );

  const getInitialData = useCallback(async () => {
    const { allSpecializations } = await getPercentileCalculation();

    const filteredSpecializations = hasFullAccess
      ? allSpecializations
      : allSpecializations.filter((p: any) => RESTRICTED_POSITION_IDS.includes(Number(p.id)));

    const initialPositionData = filteredSpecializations.find((p: any) => String(p.value) === String(initialPosition));

    return {
      position: initialPositionData?.value ?? null,
      compensationType: BASE_AMOUNT_TYPES_ALIASES.MONTHLY,
      compensationSum: '',
      currency: CURRENCY_TYPES.USD,
      positionsAndTechnologies: filteredSpecializations,
    };
  }, [initialPosition, getPercentileCalculation, hasFullAccess]);

  if (!canCalculatePercentile) {
    return null;
  }

  return (
    <ModalFormButton
      label="Calculate percentile"
      outline
      size={size}
      backdrop
      paperStyles={{ backgroundColor: 'var(--mui-palette-background-paper)' }}
      formTitle="Compensation percentile calculator"
      modalSize="sm"
      getValidationSchemaFunc={() => Promise.resolve(calculatePrecentileShema)}
      getDataFunc={getInitialData}
      submitFormFunc={calculateAction}
      afterSubmit={{
        execute: (res: any) => setPercentile(String(res.percentile)),
      }}
      isCloseAfterSubmit={false}
      isHideDefaultButtons
      closeEffect={() => {
        setPercentile('');
        setShowPercentData(null);
      }}
    >
      <CalculateForm showPercentData={showPercentData} percentile={percentile} hasFullAccess={hasFullAccess} />
    </ModalFormButton>
  );
};

export default CalculatePercentileModalButton;
